// // src/hooks/useCollabScale.js
// import * as Y from "yjs";
// import { WebsocketProvider } from "y-websocket";
// import { useEffect, useRef } from "react";

// /**
//  * Real-time sync for { unit, scale, scaleLength }.
//  * Usage:
//  *   const { syncScale } = useCollabScale(projectId, (remote) => {
//  *     setUnit(remote.unit ?? "px");
//  *     setScale(remote.scale ?? 1);
//  *     setScaleLength(remote.scaleLength ?? 100);
//  *   });
//  *   // whenever you change any of them locally:
//  *   syncScale({ unit, scale, scaleLength });
//  */
// export default function useCollabScale(projectId, onRemoteUpdate) {
//   const ydocRef = useRef(null);
//   const providerRef = useRef(null);
//   const yMapRef = useRef(null);

//   // keep latest callback without recreating the connection
//   const cbRef = useRef(onRemoteUpdate);
//   useEffect(() => {
//     cbRef.current = onRemoteUpdate;
//   }, [onRemoteUpdate]);

//   useEffect(() => {
//     if (!projectId) return;

//     const ydoc = new Y.Doc();
//     const raw = process.env.REACT_APP_SOCKET_URL || "ws://localhost:1234";
//     const socketUrl = raw.replace(/['"\s]+/g, "").replace(/\/+$/g, "");
//     const room = String(projectId).trim();

//     const provider = new WebsocketProvider(socketUrl, room, ydoc);

//     // store these three in a single Y.Map named "scale"
//     const yMap = ydoc.getMap("scale");

//     const emit = () => {
//       const unit = yMap.get("unit");
//       const scale = yMap.get("scale");
//       const scaleLength = yMap.get("scaleLength");
//       cbRef.current?.({
//         unit,
//         scale,
//         scaleLength,
//       });
//     };

//     // observe any key change on the map
//     const observer = () => emit();
//     yMap.observe(observer);
//     // initial hydrate (will call onRemoteUpdate with current values or undefineds)
//     emit();

//     ydocRef.current = ydoc;
//     providerRef.current = provider;
//     yMapRef.current = yMap;

//     return () => {
//       try {
//         yMap.unobserve(observer);
//       } catch {}
//       try {
//         provider.destroy();
//       } catch {}
//       try {
//         ydoc.destroy();
//       } catch {}
//       ydocRef.current = null;
//       providerRef.current = null;
//       yMapRef.current = null;
//     };
//   }, [projectId]);

//   // atomic update of any subset of {unit, scale, scaleLength}
//   const syncScale = (partial = {}) => {
//     const yMap = yMapRef.current;
//     const ydoc = ydocRef.current;
//     if (!yMap || !ydoc) return;

//     const { unit, scale, scaleLength } = partial;

//     ydoc.transact(() => {
//       if (unit !== undefined) yMap.set("unit", unit);
//       if (scale !== undefined) yMap.set("scale", scale);
//       if (scaleLength !== undefined) yMap.set("scaleLength", scaleLength);
//     });
//   };

//   return { syncScale };
// }

import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { useEffect, useRef } from "react";

export default function useCollabScale(projectId, onRemoteUpdate) {
  const ydocRef = useRef(null);
  const providerRef = useRef(null);
  const yMapRef = useRef(null);

  const cbRef = useRef(onRemoteUpdate);
  useEffect(() => {
    cbRef.current = onRemoteUpdate;
  }, [onRemoteUpdate]);

  // ignore our own echo to prevent flicker/races while dragging
  const lastSentRef = useRef("");

  const stableHash = (o) => {
    try {
      return JSON.stringify(o);
    } catch {
      return String(Date.now());
    }
  };

  useEffect(() => {
    if (!projectId) return;

    const ydoc = new Y.Doc();
    const raw = process.env.REACT_APP_SOCKET_URL || "ws://localhost:1234";
    const socketUrl = raw.replace(/['\"\s]+/g, "").replace(/\/+$/g, "");
    const room = String(projectId).trim();

    const provider = new WebsocketProvider(socketUrl, room, ydoc);
    const yMap = ydoc.getMap("scale"); // same map you already use

    const emit = () => {
      const payload = {
        unit: yMap.get("unit"),
        scale: yMap.get("scale"),
        scaleLength: yMap.get("scaleLength"),
        scalePosX: yMap.get("scalePosX"),
        scalePosY: yMap.get("scalePosY"),
        scaleAngle: yMap.get("scaleAngle"),
      };
      const h = stableHash(payload);
      if (h === lastSentRef.current) return; // ignore our own
      cbRef.current?.(payload);
    };

    yMap.observe(emit);
    emit();

    ydocRef.current = ydoc;
    providerRef.current = provider;
    yMapRef.current = yMap;
    return () => {
      try {
        yMap.unobserve(emit);
      } catch {}
      try {
        provider.destroy();
      } catch {}
      try {
        ydoc.destroy();
      } catch {}
      ydocRef.current = null;
      providerRef.current = null;
      yMapRef.current = null;
    };
  }, [projectId]);

  // partial update; only provided keys are written
  const syncScale = (partial = {}) => {
    const yMap = yMapRef.current;
    const ydoc = ydocRef.current;
    if (!yMap || !ydoc) return;

    // build full next payload to generate an echo-guard hash
    const next = {
      unit: yMap.get("unit"),
      scale: yMap.get("scale"),
      scaleLength: yMap.get("scaleLength"),
      scalePosX: yMap.get("scalePosX"),
      scalePosY: yMap.get("scalePosY"),
      scaleAngle: yMap.get("scaleAngle"),
      ...partial,
    };
    const h = stableHash(next);

    ydoc.transact(() => {
      if (partial.unit !== undefined) yMap.set("unit", partial.unit);
      if (partial.scale !== undefined) yMap.set("scale", partial.scale);
      if (partial.scaleLength !== undefined)
        yMap.set("scaleLength", partial.scaleLength);
      if (partial.scalePosX !== undefined)
        yMap.set("scalePosX", partial.scalePosX);
      if (partial.scalePosY !== undefined)
        yMap.set("scalePosY", partial.scalePosY);
      if (partial.scaleAngle !== undefined)
        yMap.set("scaleAngle", partial.scaleAngle);
    });

    lastSentRef.current = h;
  };

  return { syncScale };
}
